// useBirthChart - Hook for managing birth chart state and API calls
import { useState, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { doc, setDoc, collection, getDocs, query, orderBy, serverTimestamp } from 'firebase/firestore';
import { functions, db, appId } from '../utils/firebase';

export const useBirthChart = (currentUser = null, tonePrefix = '') => {
    // Form state
    const [birthDate, setBirthDate] = useState('');
    const [birthTime, setBirthTime] = useState('');
    const [birthLocation, setBirthLocation] = useState('');
    const [coordinates, setCoordinates] = useState(null); // { latitude, longitude }
    const [timezone, setTimezone] = useState('');
    const [unknownTime, setUnknownTime] = useState(false);

    // Location search state
    const [locationResults, setLocationResults] = useState([]);
    const [isSearchingLocation, setIsSearchingLocation] = useState(false);

    // Chart data state
    const [chartData, setChartData] = useState(null);
    const [interpretation, setInterpretation] = useState('');
    const [chartName, setChartName] = useState(''); // Name for the chart (e.g., "My Chart" or friend's name)

    // Save state
    const [isSaving, setIsSaving] = useState(false);
    const [isSaved, setIsSaved] = useState(false);
    const [savedChartId, setSavedChartId] = useState(null);
    const [savedCharts, setSavedCharts] = useState([]);
    const [isLoadingSavedCharts, setIsLoadingSavedCharts] = useState(false);

    // Loading and error state
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingInterpretation, setIsLoadingInterpretation] = useState(false);
    const [error, setError] = useState('');

    // Form step (for multi-step wizard)
    const [currentStep, setCurrentStep] = useState(0); // 0: date, 1: time, 2: location, 3: confirm

    /**
     * Search for locations using geocoding
     */
    const searchLocation = useCallback(async (query) => {
        if (!query || query.length < 2) {
            setLocationResults([]);
            return;
        }

        setIsSearchingLocation(true);
        setError('');

        try {
            const geocodeFunction = httpsCallable(functions, 'geocodeLocation');
            const result = await geocodeFunction({ query });
            setLocationResults(result.data || []);
        } catch (err) {
            console.error('Geocoding error:', err);
            setError('Failed to search location. Please try again.');
            setLocationResults([]);
        } finally {
            setIsSearchingLocation(false);
        }
    }, []);

    /**
     * Select a location from search results
     */
    const selectLocation = useCallback(async (location) => {
        setBirthLocation(location.displayName);
        setCoordinates({
            latitude: location.latitude,
            longitude: location.longitude
        });
        if (location.timezone) {
            setTimezone(location.timezone);
        }
        setLocationResults([]);

        // Get timezone for the location
        if (location.timezone) {
            return;
        }

        try {
            const getTimezoneFunction = httpsCallable(functions, 'getTimezone');
            const result = await getTimezoneFunction({
                latitude: location.latitude,
                longitude: location.longitude
            });
            setTimezone(result.data.timezone);
        } catch (err) {
            console.error('Timezone lookup error:', err);
            // Estimate timezone from longitude
            const offset = Math.round(location.longitude / 15);
            setTimezone(`Etc/GMT${offset >= 0 ? '-' : '+'}${Math.abs(offset)}`);
        }
    }, []);

    /**
     * Calculate the natal chart
     */
    const calculateChart = useCallback(async () => {
        if (!birthDate || (!unknownTime && !birthTime) || !coordinates) {
            setError('Please fill in all required fields.');
            return null;
        }

        setIsLoading(true);
        setError('');
        setChartData(null);

        try {
            const calculateFunction = httpsCallable(functions, 'calculateNatalChart');
            const result = await calculateFunction({
                birthDate,
                birthTime: unknownTime ? '12:00' : birthTime,
                latitude: coordinates.latitude,
                longitude: coordinates.longitude,
                timezone: timezone || 'UTC',
                location: birthLocation,
                unknownTime
            });

            setChartData(result.data);
            return result.data;
        } catch (err) {
            console.error('Chart calculation error:', err);
            setError('Failed to calculate birth chart. Please try again.');
            return null;
        } finally {
            setIsLoading(false);
        }
    }, [birthDate, birthTime, unknownTime, coordinates, timezone, birthLocation]);

    /**
     * Get AI interpretation for the chart
     */
    const getInterpretation = useCallback(async (chartDataToInterpret = null) => {
        const data = chartDataToInterpret || chartData;
        if (!data) {
            setError('No chart data available for interpretation.');
            return;
        }

        setIsLoadingInterpretation(true);
        setError('');

        try {
            const getTarotReadingFunction = httpsCallable(functions, 'getTarotReading');

            // Build a prompt for birth chart interpretation
            const planets = data.planets.map(p => `${p.name} in ${p.sign} (${p.degree}°, House ${p.house})`).join(', ');
            const toneInstructions = tonePrefix || 'You are a mystical astrologer.';
            const prompt = `${toneInstructions} Provide a comprehensive birth chart interpretation for someone with:
            
Ascendant (Rising Sign): ${data.ascendant.sign} (${data.ascendant.formattedPosition})
Sun Sign: ${data.planets.find(p => p.name === 'Sun')?.sign || 'Unknown'}
Moon Sign: ${data.planets.find(p => p.name === 'Moon')?.sign || 'Unknown'}

Planetary Positions: ${planets}

Key Aspects: ${data.aspects.slice(0, 5).map(a => `${a.planet1} ${a.aspectSymbol || a.symbol || a.aspect} ${a.planet2}`).join(', ')}

Provide a mystical, insightful interpretation covering:
1. The "Big Three" (Sun, Moon, Rising) and overall energy
2. Key personality traits and life themes
3. Strengths and growth opportunities
4. A cosmic message for their journey

About 4-5 paragraphs.`;

            const result = await getTarotReadingFunction({ promptText: prompt });
            const text = result.data?.candidates?.[0]?.content?.parts?.[0]?.text || '';
            setInterpretation(text);
            return text;
        } catch (err) {
            console.error('Interpretation error:', err);
            setError('Failed to generate interpretation. Please try again.');
            return null;
        } finally {
            setIsLoadingInterpretation(false);
        }
    }, [chartData, tonePrefix]);

    /**
     * Get deeper insight for a specific planet
     */
    const getPlanetInsight = useCallback(async (planet) => {
        if (!chartData) return null;

        try {
            const getTarotReadingFunction = httpsCallable(functions, 'getTarotReading');

            const toneInstructions = tonePrefix || 'You are a mystical astrologer.';
            const prompt = `${toneInstructions} Provide a deeper insight for ${planet.name} in ${planet.sign} in the ${planet.house}${getHouseOrdinal(planet.house)} House.

This placement suggests: ${planet.formattedPosition}

Explain:
1. What this means for their personality and life path
2. How this planet expresses itself in this sign
3. The house influence on this placement
4. Practical guidance for working with this energy

About 2-3 paragraphs.`;

            const result = await getTarotReadingFunction({ promptText: prompt });
            return result.data?.candidates?.[0]?.content?.parts?.[0]?.text || '';
        } catch (err) {
            console.error('Planet insight error:', err);
            return 'Failed to retrieve cosmic insight for this placement.';
        }
    }, [chartData, tonePrefix]);

    /**
     * Save the birth chart to Firestore
     */
    const saveChart = useCallback(async (name = 'My Chart') => {
        if (!currentUser || currentUser.isAnonymous) {
            setError('Please sign in to save your birth chart.');
            return null;
        }

        if (!chartData) {
            setError('No chart data to save.');
            return null;
        }

        setIsSaving(true);
        setError('');

        try {
            const chartId = `chart_${Date.now()}`;
            const chartsPath = `artifacts/${appId}/users/${currentUser.uid}/birthCharts/${chartId}`;

            await setDoc(doc(db, chartsPath), {
                name: name || 'My Chart',
                chartData,
                interpretation,
                birthDate,
                birthTime: unknownTime ? '12:00' : birthTime,
                birthLocation,
                coordinates,
                timezone,
                unknownTime,
                createdAt: serverTimestamp(),
                userId: currentUser.uid
            });

            setChartName(name);
            setIsSaved(true);
            setSavedChartId(chartId);
            return chartId;
        } catch (err) {
            console.error('Save chart error:', err);
            setError('Failed to save chart. Please try again.');
            return null;
        } finally {
            setIsSaving(false);
        }
    }, [currentUser, chartData, interpretation, birthDate, birthTime, birthLocation, coordinates, timezone, unknownTime]);

    /**
     * Load all saved charts for the user
     */
    const loadSavedCharts = useCallback(async () => {
        if (!currentUser || currentUser.isAnonymous) {
            setSavedCharts([]);
            return [];
        }

        setIsLoadingSavedCharts(true);

        try {
            const chartsPath = `artifacts/${appId}/users/${currentUser.uid}/birthCharts`;
            const chartsRef = collection(db, chartsPath);
            const q = query(chartsRef, orderBy('createdAt', 'desc'));
            const snapshot = await getDocs(q);

            const charts = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                createdAt: doc.data().createdAt?.toDate?.() || new Date()
            }));

            setSavedCharts(charts);
            return charts;
        } catch (err) {
            console.error('Load saved charts error:', err);
            setSavedCharts([]);
            return [];
        } finally {
            setIsLoadingSavedCharts(false);
        }
    }, [currentUser]);

    /**
     * Load a specific saved chart
     */
    const loadSavedChart = useCallback((chart) => {
        if (!chart) return;

        setChartData(chart.chartData);
        setInterpretation(chart.interpretation || '');
        setBirthDate(chart.birthDate || '');
        setBirthTime(chart.birthTime || '');
        setBirthLocation(chart.birthLocation || '');
        setCoordinates(chart.coordinates || null);
        setTimezone(chart.timezone || '');
        setUnknownTime(chart.unknownTime || false);
        setChartName(chart.name || '');
        setIsSaved(true);
        setSavedChartId(chart.id);
        setCurrentStep(0);
    }, []);

    /**
     * Reset all state
     */
    const reset = useCallback(() => {
        setBirthDate('');
        setBirthTime('');
        setBirthLocation('');
        setCoordinates(null);
        setTimezone('');
        setUnknownTime(false);
        setLocationResults([]);
        setChartData(null);
        setInterpretation('');
        setChartName('');
        setIsSaving(false);
        setIsSaved(false);
        setSavedChartId(null);
        setIsLoading(false);
        setError('');
        setCurrentStep(0);
    }, []);

    /**
     * Navigation helpers
     */
    const nextStep = useCallback(() => {
        setCurrentStep(prev => Math.min(prev + 1, 3));
    }, []);

    const prevStep = useCallback(() => {
        setCurrentStep(prev => Math.max(prev - 1, 0));
    }, []);

    const goToStep = useCallback((step) => {
        setCurrentStep(Math.max(0, Math.min(step, 3)));
    }, []);

    /**
     * Validation
     */
    const isStepValid = useCallback((step) => {
        switch (step) {
            case 0: return !!birthDate;
            case 1: return unknownTime || !!birthTime;
            case 2: return !!coordinates;
            case 3: return true;
            default: return false;
        }
    }, [birthDate, birthTime, unknownTime, coordinates]);

    const canProceed = isStepValid(currentStep);

    return {
        // Form state
        birthDate, setBirthDate,
        birthTime, setBirthTime,
        birthLocation, setBirthLocation,
        coordinates, setCoordinates,
        timezone, setTimezone,
        unknownTime, setUnknownTime,
        chartName, setChartName,

        // Location search
        locationResults,
        isSearchingLocation,
        searchLocation,
        selectLocation,

        // Chart data
        chartData,
        interpretation,

        // Save state
        isSaving,
        isSaved,
        savedChartId,
        savedCharts,
        isLoadingSavedCharts,

        // Loading/error
        isLoading,
        isLoadingInterpretation,
        error,
        setError,

        // Navigation
        currentStep,
        nextStep,
        prevStep,
        goToStep,
        canProceed,
        isStepValid,

        // Actions
        calculateChart,
        getInterpretation,
        getPlanetInsight,
        saveChart,
        loadSavedCharts,
        loadSavedChart,
        reset
    };
};

// Helper function for ordinal suffixes
const getHouseOrdinal = (num) => {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = num % 100;
    return s[(v - 20) % 10] || s[v] || s[0];
};

export default useBirthChart;
